<template>
  <DashboardWidget
    title="Workflows"
    icon="fa fa-random"
    :view-all-link="{ name: 'Workflow2List' }"
    :loading="loading"
    :is-empty="!hasWorkflows"
    empty-icon="fa fa-random"
    empty-title="No workflows yet"
    empty-text="Automate your practice with workflows"
  >
    <div class="workflows-summary">
      <!-- Stats Row -->
      <div class="stats-row">
        <div class="stat-item">
          <span class="stat-value active">{{ activeWorkflowsCount }}</span>
          <span class="stat-label">Active</span>
        </div>
        <div class="stat-item">
          <span class="stat-value inactive">{{ inactiveWorkflowsCount }}</span>
          <span class="stat-label">Inactive</span>
        </div>
        <div class="stat-item">
          <span class="stat-value total">{{ totalWorkflowsCount }}</span>
          <span class="stat-label">Total</span>
        </div>
      </div>

      <!-- Recent Workflows List -->
      <div v-if="recentWorkflows.length > 0" class="workflows-list">
        <div class="list-header">Recently Updated</div>
        <div
          v-for="workflow in recentWorkflows"
          :key="workflow.WorkflowID"
          class="list-item workflow-item"
          @click="goToWorkflow(workflow)"
        >
          <div class="item-avatar workflow-avatar" :class="{ active: workflow.Active }">
            <i class="fa fa-cogs"></i>
          </div>
          <div class="item-content">
            <div class="item-header">
              <span class="item-title">{{ workflow.Name }}</span>
              <span class="item-badge" :class="{ active: workflow.Active, inactive: !workflow.Active }">
                {{ workflow.Active ? 'Active' : 'Inactive' }}
              </span>
            </div>
            <p class="item-preview">
              <span v-if="workflow.TriggerOn">Trigger: {{ formatTrigger(workflow.TriggerOn) }}</span>
              <span v-if="workflow.LastUpdated"> &bull; Updated {{ getTimeAgo(workflow.LastUpdated) }}</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  </DashboardWidget>
</template>

<script>
import { mapActions } from 'vuex';
import DashboardWidget from './DashboardWidget.vue';
import moment from 'moment-timezone';
import mixin from '../../../mixin.js';

export default {
  name: 'WorkflowWidget',

  components: {
    DashboardWidget
  },

  mixins: [mixin],

  props: {
    maxItems: {
      type: Number,
      default: 3
    }
  },

  data() {
    return {
      isLoadingWorkflows: true,
      workflows: []
    };
  },

  computed: {
    loading() {
      return this.isLoadingWorkflows;
    },

    hasWorkflows() {
      return this.workflows && this.workflows.length > 0;
    },

    activeWorkflowsCount() {
      return this.workflows.filter(w => w.Active === true || w.Active === 'true').length;
    },

    inactiveWorkflowsCount() {
      return this.workflows.filter(w => w.Active === false || w.Active === 'false' || !w.Active).length;
    },

    totalWorkflowsCount() {
      return this.workflows.length;
    },

    recentWorkflows() {
      if (!this.workflows) return [];
      // Sort by LastUpdated descending, take top N
      return [...this.workflows]
        .sort((a, b) => {
          const dateA = new Date(a.LastUpdated || a.CreatedOn || 0);
          const dateB = new Date(b.LastUpdated || b.CreatedOn || 0);
          return dateB - dateA;
        })
        .slice(0, this.maxItems);
    }
  },

  async mounted() {
    await this.loadWorkflows();
  },

  methods: {
    ...mapActions('workflow', ['getAllWorkflows']),

    async loadWorkflows() {
      this.isLoadingWorkflows = true;
      try {
        const result = await this.getAllWorkflows({ siteID: this.siteID });
        this.workflows = result || [];
      } catch (error) {
        console.error('Error loading workflows:', error);
        this.workflows = [];
      } finally {
        this.isLoadingWorkflows = false;
      }
    },

    goToWorkflow(workflow) {
      this.$router.push({ name: 'ViewWorkflow2', params: { workflowID: workflow.WorkflowID } });
    },

    getTimeAgo(date) {
      if (!date) return '';
      return moment(date).fromNow();
    },

    formatTrigger(trigger) {
      if (!trigger) return 'Manual';
      // Format trigger type for display
      const triggerMap = {
        'task-completed': 'Task Completed',
        'form-submitted': 'Form Submitted',
        'appointment-scheduled': 'Appointment',
        'patient-created': 'New Patient',
        'schedule': 'Scheduled',
        'manual': 'Manual'
      };
      return triggerMap[trigger] || trigger;
    }
  }
};
</script>

<style lang="scss" scoped>
.workflows-summary {
  .stats-row {
    display: flex;
    justify-content: space-evenly;
    padding: var(--widget-stat-padding, 12px 20px);
    border-bottom: 1px solid #e8ecf1;
    margin-bottom: 0;
    min-height: 60px;
  }

  .stat-item {
    display: block !important;
    text-align: center;
    flex: 1;
    min-width: 0;
    padding: 0 8px !important;
    border-left: none !important;
  }

  .stat-value {
    display: block;
    font-size: var(--widget-stat-size, 1.5rem);
    font-weight: 700;
    line-height: 1.2;

    &.active {
      color: #22c55e;
    }

    &.inactive {
      color: #94a3b8;
    }

    &.total {
      color: #334155;
    }
  }

  .stat-label {
    font-size: 0.6875rem;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-top: 2px;
  }

  .workflows-list {
    .list-header {
      font-size: 0.75rem;
      font-weight: 600;
      color: #64748b;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      padding: var(--widget-list-padding, 12px 20px 8px 20px);
    }
  }

  .workflow-item {
    cursor: pointer;
    transition: background-color 0.15s ease;
    padding: var(--widget-list-item-padding, 14px 20px);
    display: flex;
    align-items: flex-start;
    gap: 12px;
    border-bottom: 1px solid #e8ecf1;
    border-left: none !important;
    border-right: none !important;
    border-top: none !important;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background-color: #f8fafc;
    }
  }

  .workflow-avatar {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(100, 116, 139, 0.1);
    color: #64748b;
    border: none !important;

    &.active {
      background-color: rgba(34, 197, 94, 0.1);
      color: #22c55e;
    }

    i {
      font-size: 1rem;
    }
  }

  .item-badge {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 0.6875rem;
    font-weight: 500;

    &.active {
      background-color: rgba(34, 197, 94, 0.1);
      color: #22c55e;
    }

    &.inactive {
      background-color: rgba(100, 116, 139, 0.1);
      color: #64748b;
    }
  }
}
</style>
